import { useEffect, useMemo, useState } from "react";
import { Card, Stack, Typography } from "@altimateai/lego";
import { NoRulesImage } from "./assets";
import AddNewAccessRule from "./AddNewAccessRule";
import { Rule, AccessRuleTableRowData } from "./UserGroupDetails.types";
import { Datastore } from "@/pages/datastores/types";
import { DbtModelFiltersResponse } from "@/types/dbtModels";
import AccessRulesGroupedTable from "./AccessRulesGroupedTable";
import AccessRulesFlatTable from "./AccessRulesFlatTable";
import { DATASTORE_ENTITY_TYPE } from "./accessRules.constants";

interface AccessRulesSectionProps {
  rules?: Rule[];
  datastores?: Datastore[];
  dbtIntegrations?: DbtModelFiltersResponse["dbt_integrations"];
  getUserName: (userId: string) => string;
  canManageRules: boolean;
  isGroupDeleted: boolean;
  groupId?: string;
  refetch: () => void;
  accessRulesDisabled: boolean;
}

const AccessRulesSection = ({
  rules,
  datastores,
  dbtIntegrations,
  getUserName,
  canManageRules,
  isGroupDeleted,
  groupId,
  refetch,
  accessRulesDisabled,
}: AccessRulesSectionProps) => {
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  const groupedRules = useMemo(() => {
    if (!rules?.length) return {};
    return rules.reduce((acc: Record<string, Rule[]>, rule) => {
      const grouping = rule.grouping || "ungrouped";
      if (!acc[grouping]) {
        acc[grouping] = [];
      }
      acc[grouping].push(rule);
      return acc;
    }, {});
  }, [rules]);

  useEffect(() => {
    const groupKeys = Object.keys(groupedRules);
    if (!groupKeys.length) return;
    setExpandedGroups(prev => {
      let changed = false;
      const next = { ...prev };
      groupKeys.forEach(grouping => {
        const key = grouping || "ungrouped";
        if (typeof next[key] === "undefined") {
          next[key] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [groupedRules]);

  const tableData = useMemo(() => {
    if (!rules?.length) return [];

    const result: AccessRuleTableRowData[] = [];
    const sortedGroups = Object.entries(groupedRules).sort(
      ([groupingA, rulesA], [groupingB, rulesB]) => {
        const entityTypeA = rulesA[0]?.entity_type || "";
        const entityTypeB = rulesB[0]?.entity_type || "";

        if (entityTypeA !== entityTypeB) {
          if (entityTypeA === DATASTORE_ENTITY_TYPE && entityTypeB !== DATASTORE_ENTITY_TYPE) {
            return 1;
          }
          if (entityTypeB === DATASTORE_ENTITY_TYPE && entityTypeA !== DATASTORE_ENTITY_TYPE) {
            return -1;
          }
          return entityTypeA.localeCompare(entityTypeB);
        }

        return groupingA.localeCompare(groupingB);
      }
    );

    sortedGroups.forEach(([grouping, groupRules], groupIndex) => {
      if (!groupRules.length) return;
      const sortedRules = [...groupRules].sort((a, b) => {
        if (a.entity_type !== b.entity_type) {
          if (a.entity_type === DATASTORE_ENTITY_TYPE && b.entity_type !== DATASTORE_ENTITY_TYPE) {
            return 1;
          }
          if (b.entity_type === DATASTORE_ENTITY_TYPE && a.entity_type !== DATASTORE_ENTITY_TYPE) {
            return -1;
          }
          return a.entity_type.localeCompare(b.entity_type);
        }
        return a.rule.entity.localeCompare(b.rule.entity);
      });

      const headerItem = {
        ...sortedRules[0],
        count: sortedRules.length,
        isGroupHeader: true,
        ruleOperator: (groupIndex === 0 ? "WHERE" : "OR") as "WHERE" | "AND" | "OR",
      };
      result.push(headerItem);

      if (expandedGroups[grouping]) {
        sortedRules.slice(1).forEach(rule => {
          result.push({
            ...rule,
            ruleOperator: "AND",
          });
        });
      }
    });

    return result;
  }, [rules, groupedRules, expandedGroups]);

  const nonGroupedRulesData = useMemo(() => {
    if (!rules?.length) return [];
    return rules.map((rule, index) => ({
      ...rule,
      ruleOperator: (index === 0 ? "WHERE" : "OR") as "WHERE" | "AND" | "OR",
    }));
  }, [rules]);

  const hasGroupedRules =
    Boolean(rules?.length) && Object.keys(groupedRules).length > 0 && tableData.length > 0;

  const toggleGroupExpansion = (groupName: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  if (accessRulesDisabled) {
    return (
      <section aria-label="Access Rules" className="al-bg-light al-p-3 al-rounded">
        <Stack className="al-justify-between al-mb-3">
          <Typography variant="section-heading">Access Rules</Typography>
        </Stack>
        <Card>
          <Stack direction="column" justify="center" align="center" className="al-p-4 al-rounded">
            <NoRulesImage />
            Access rules are disabled, please contact your administrator.
          </Stack>
        </Card>
      </section>
    );
  }

  return (
    <section aria-label="Access Rules" className="al-bg-light al-p-3 al-rounded">
      <Stack className="al-justify-between al-mb-3">
        <Typography variant="section-heading">Access Rules</Typography>
        {!isGroupDeleted && canManageRules && groupId && <AddNewAccessRule groupId={groupId} />}
      </Stack>

      {(!rules || rules.length === 0) && (
        <Card>
          <Stack direction="column" justify="center" align="center" className="al-p-4 al-rounded">
            <NoRulesImage />
            No rules found! Please re upload CSV with team information and rules
          </Stack>
        </Card>
      )}

      {rules && rules.length > 0 && (
        <div className="al-w-full">
          {hasGroupedRules ? (
            <AccessRulesGroupedTable
              items={tableData}
              datastores={datastores}
              dbtIntegrations={dbtIntegrations}
              expandedGroups={expandedGroups}
              toggleGroup={toggleGroupExpansion}
              getUserName={getUserName}
              canManageRules={canManageRules}
              groupId={groupId}
              refetch={refetch}
            />
          ) : (
            <AccessRulesFlatTable
              items={nonGroupedRulesData}
              datastores={datastores}
              getUserName={getUserName}
              canManageRules={canManageRules}
              groupId={groupId}
              refetch={refetch}
            />
          )}
        </div>
      )}
    </section>
  );
};

export default AccessRulesSection;
